import dayjs from 'dayjs';
import { formatBookingCurrency } from './formatBookingCurrency';
import { getBookingReservationApplicantDisplay } from './getBookingReservationApplicantDisplay';
import type {
  BookingReservationInvoiceLookup,
  BookingReservationPartyLookup,
  BookingReservationRow,
} from '../Types/bookingReservationTable.types';

export interface BookingReservationsPdfModelParams {
  reservations: BookingReservationRow[];
  parties: BookingReservationPartyLookup[];
  invoicesByReservationId: Record<number, BookingReservationInvoiceLookup[]>;
  offerName?: string;
  dateFrom?: string;
  dateTo?: string;
}

export interface BookingReservationsPdfAmount {
  currency: string;
  amount: number;
}

export interface BookingReservationsPdfRow {
  lp: number;
  invoiceNumber: string;
  buyer: string;
  amounts: BookingReservationsPdfAmount[];
}

export interface BookingReservationsPdfModel {
  title: string;
  termLabel: string;
  rows: BookingReservationsPdfRow[];
  totals: BookingReservationsPdfAmount[];
}

const formatDate = (value?: string): string => {
  if (!value) {
    return '-';
  }

  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format('DD.MM.YYYY') : '-';
};

const resolveInvoiceNumberLabel = (invoices: BookingReservationInvoiceLookup[]): string => {
  const numbers = invoices
    .map((invoice) => invoice.number?.trim() ?? '')
    .filter((value) => value.length > 0);

  if (numbers.length === 0) {
    return '-';
  }

  return numbers.join(', ');
};

const DEFAULT_PDF_CURRENCY = 'PLN';

export const sumInvoiceAmountsByCurrency = (
  invoices: BookingReservationInvoiceLookup[]
): BookingReservationsPdfAmount[] => {
  const sums = new Map<string, number>();
  invoices.forEach((invoice) => {
    const currency = invoice.currency?.trim().toUpperCase() || DEFAULT_PDF_CURRENCY;
    sums.set(currency, (sums.get(currency) ?? 0) + (invoice.totalGross ?? 0));
  });

  const currencyRank = (currency: string): number => (currency === DEFAULT_PDF_CURRENCY ? 0 : 1);

  return Array.from(sums, ([currency, amount]) => ({ currency, amount })).sort(
    (a, b) =>
      currencyRank(a.currency) - currencyRank(b.currency) || a.currency.localeCompare(b.currency)
  );
};

const withZeroFallback = (
  amounts: BookingReservationsPdfAmount[]
): BookingReservationsPdfAmount[] =>
  amounts.length > 0 ? amounts : [{ currency: DEFAULT_PDF_CURRENCY, amount: 0 }];

export const formatBookingReservationsPdfAmounts = (
  amounts: BookingReservationsPdfAmount[]
): string =>
  amounts.map(({ amount, currency }) => formatBookingCurrency(amount, currency)).join(' + ');

export const buildBookingReservationsPdfModel = ({
  reservations,
  parties,
  invoicesByReservationId,
  offerName,
  dateFrom,
  dateTo,
}: BookingReservationsPdfModelParams): BookingReservationsPdfModel => {
  const rows = reservations.map((reservation, index) => {
    const invoices = invoicesByReservationId[reservation.id] ?? [];
    const buyer = getBookingReservationApplicantDisplay(reservation, parties).title || '-';

    return {
      lp: index + 1,
      invoiceNumber: resolveInvoiceNumberLabel(invoices),
      buyer,
      amounts: withZeroFallback(sumInvoiceAmountsByCurrency(invoices)),
    };
  });

  return {
    title: offerName?.trim() ? `Nazwa oferty: ${offerName.trim()}` : 'Nazwa oferty: -',
    termLabel: `Termin: ${formatDate(dateFrom)} - ${formatDate(dateTo)}`,
    rows,
    totals: withZeroFallback(
      sumInvoiceAmountsByCurrency(
        reservations.flatMap((reservation) => invoicesByReservationId[reservation.id] ?? [])
      )
    ),
  };
};
